import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Link, Camera, Barcode as BarcodeIcon, Image, Check, X, AlertCircle } from 'lucide-react';

const resizeImage = (dataUrl, maxSize = 120) =>
  new Promise((resolve) => {
    const img = document.createElement('img');
    img.onload = () => {
      const ratio = Math.min(maxSize / img.width, maxSize / img.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

const ScanScreenQuadrant = () => {
  const { setCurrentScreen, setActiveTab, scanData, updateScanData, setCurrentProduct } = useApp();
  const [errorDialog, setErrorDialog] = useState(null);

  const clearMethod = (method) => {
    updateScanData({
      [method]: { active: false, data: '', images: [] }
    });
  };

  const activeMethods = Object.values(scanData).filter(m => m.active).length;
  const canAnalyze = activeMethods > 0;

  const handleAnalyze = async () => {
    if (!canAnalyze) return;
    
    // Navigate to analyzing screen first
    setCurrentScreen('analyzing');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';

      // Validate and filter images, only accept valid data URLs
      const validImage = (s) => typeof s === 'string' && s.startsWith('data:image/') && s.includes('base64,');

      // Build request body from scanData
      const requestBody = {};

      const tagImages = (scanData.tag.images || []).filter(validImage);
      if (tagImages.length > 0) requestBody.tag_images = tagImages;

      const clothingImages = (scanData.photos.images || []).filter(validImage);
      if (clothingImages.length > 0) requestBody.clothing_images = clothingImages;

      if (scanData.barcode.data) requestBody.barcode = scanData.barcode.data;
      if (scanData.url.data) requestBody.product_url = scanData.url.data;

      if (!requestBody.tag_images && !requestBody.clothing_images && !requestBody.barcode && !requestBody.product_url) {
        throw new Error('No valid images or URL found. Please add a scan method.');
      }

      // Call backend API
      let body;
      try {
        body = JSON.stringify(requestBody);
      } catch (e) {
        throw new Error('Failed to prepare image data: ' + e.message);
      }

      let response;
      try {
        response = await fetch(API_URL + '/api/scan/full/v2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      } catch (e) {
        throw new Error('Network error, could not reach server: ' + e.message);
      }

      if (!response.ok) {
        let detail = 'Analysis failed';
        try { detail = (await response.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      
      const result = await response.json();
      
      // Save result to AppContext as currentProduct
      // Prefer server-stored image URL; fall back to local base64 for thumbnail
      const serverImageUrl = result.stored_image_url || null;
      const rawImageUrl = scanData.photos.images[0] || scanData.tag.images[0] || null;
      const thumbnailUrl = serverImageUrl || (rawImageUrl ? await resizeImage(rawImageUrl) : null);
      const productData = {
        id: result.scan_id || result.id || Date.now().toString(),
        brand: result.tag_scan?.brand || result.clothing_analysis?.brand || 'Unknown',
        name: result.tag_scan?.product_name || result.clothing_analysis?.category || 'Garment',
        category: result.clothing_analysis?.category || 'Apparel',
        imageUrl: serverImageUrl || rawImageUrl,
        thumbnailUrl,
        composition: (() => {
          if (result.tag_scan?.composition) return result.tag_scan.composition;
          const ft = (result.clothing_analysis?.fabric_type || '').trim();
          const isSimpleFiber = /^(cotton|polyester|wool|silk|linen|cashmere|nylon|acrylic|viscose|rayon|elastane|spandex|lyocell|tencel|modal|hemp|bamboo)$/i.test(ft);
          return isSimpleFiber ? ft + ' 100%' : null;
        })(),
        scores: {
          fabric: result.fabric_intelligence?.overall_score || 60,
          sustainability: result.sustainability_details?.overall_score || 60,
        },
        fabricIntelligence: result.fabric_intelligence,
        sustainabilityDetails: result.sustainability_details,
        alternatives: result.alternatives || [],
        tagScan: result.tag_scan,
        clothingAnalysis: result.clothing_analysis
      };
      
      // Save to AppContext
      setCurrentProduct(productData);
      
      // Save to sessionStorage WITHOUT images (to avoid quota exceeded)
      const lightData = {
        ...productData,
        imageUrl: productData.imageUrl, // Keep one image URL
        // Remove heavy base64 image arrays
        allImages: undefined
      };
      sessionStorage.setItem('currentScanResult', JSON.stringify(lightData));
      
      // Navigate to Canva results screen
      setCurrentScreen('results-canva');
      
    } catch (error) {
      console.error('Analysis error:', error);
      const raw = error.message || '';
      let friendly = raw;
      if (raw.includes('Network error') || raw.includes('could not reach server') || raw.includes('Failed to fetch')) {
        friendly = 'Unable to connect. Please check your internet and try again.';
      } else if (raw.includes('product page') || raw.includes('direct product link')) {
        friendly = raw; // already user-friendly from backend
      } else if (!raw || raw === 'Analysis failed') {
        friendly = 'Something went wrong. Please try again.';
      }
      setErrorDialog(friendly);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-white pb-28">
      <div className="px-6 pt-6 pb-4 flex items-center gap-4 border-b border-black/10">
        <button
          onClick={() => setActiveTab('home')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <button onClick={() => setActiveTab('home')} className="hover:opacity-80 transition-opacity">
          <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" />
        </button>
      </div>

      <div className="px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide mb-2">Scan Garment</h1>
        <p className="text-sm text-black/72 mb-1">Pick at least one method</p>
        <p className="text-xs text-black/55">Select multiple for higher accuracy</p>
      </div>

      <div className="px-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setCurrentScreen('tag-capture')}
            className={'aspect-square rounded-2xl p-4 transition-all flex flex-col items-center justify-center relative ' + (scanData.tag.active ? 'bg-black text-white' : 'bg-white border-2 border-black/12 text-black hover:border-black/30')}
          >
            {scanData.tag.active && (
              <>
                <button onClick={(e) => { e.stopPropagation(); clearMethod('tag'); }} className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <X size={14} className="text-white" />
                </button>
                <div className="absolute top-2 left-2">
                  <Check size={16} className="text-white" />
                </div>
              </>
            )}
            <Camera size={32} className={(scanData.tag.active ? 'text-white' : 'text-black') + ' mb-3'} strokeWidth={1.5} />
            <p className={'text-sm font-bold uppercase mb-1 ' + (scanData.tag.active ? 'text-white' : 'text-black')}>GARMENT TAG</p>
            <p className={'text-xs text-center ' + (scanData.tag.active ? 'text-white/70' : 'text-black/55')}>
              {scanData.tag.active ? scanData.tag.images.length + ' Added' : 'Scan Label'}
            </p>
          </button>

          <button
            onClick={() => setCurrentScreen('photo-capture')}
            className={'aspect-square rounded-2xl p-4 transition-all flex flex-col items-center justify-center relative ' + (scanData.photos.active ? 'bg-black text-white' : 'bg-white border-2 border-black/12 text-black hover:border-black/30')}
          >
            {scanData.photos.active && (
              <>
                <button onClick={(e) => { e.stopPropagation(); clearMethod('photos'); }} className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <X size={14} className="text-white" />
                </button>
                <div className="absolute top-2 left-2">
                  <Check size={16} className="text-white" />
                </div>
              </>
            )}
            <Image size={32} className={(scanData.photos.active ? 'text-white' : 'text-black') + ' mb-3'} strokeWidth={1.5} />
            <p className={'text-sm font-bold uppercase mb-1 ' + (scanData.photos.active ? 'text-white' : 'text-black')}>PHOTOS</p>
            <p className={'text-xs text-center ' + (scanData.photos.active ? 'text-white/70' : 'text-black/55')}>
              {scanData.photos.active ? scanData.photos.images.length + ' Added' : 'Add Clothing'}
            </p>
          </button>

          {/* BARCODE Quadrant - Bottom Left */}
          <button
            onClick={() => setCurrentScreen('barcode-capture')}
            className={'aspect-square rounded-2xl p-4 transition-all flex flex-col items-center justify-center relative ' + (scanData.barcode.active ? 'bg-black text-white' : 'bg-white border-2 border-black/12 text-black hover:border-black/30')}
          >
            {scanData.barcode.active && (
              <>
                <button onClick={(e) => { e.stopPropagation(); clearMethod('barcode'); }} className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <X size={14} className="text-white" />
                </button>
                <div className="absolute top-2 left-2">
                  <Check size={16} className="text-white" />
                </div>
              </>
            )}
            <BarcodeIcon size={32} className={(scanData.barcode.active ? 'text-white' : 'text-black') + ' mb-3'} strokeWidth={1.5} />
            <p className={'text-sm font-bold uppercase mb-1 ' + (scanData.barcode.active ? 'text-white' : 'text-black')}>BARCODE</p>
            <p className={'text-xs text-center ' + (scanData.barcode.active ? 'text-white/70' : 'text-black/55')}>
              {scanData.barcode.active ? 'Barcode Added' : 'Scan Code'}
            </p>
          </button>

          {/* URL Quadrant - Bottom Right */}
          <button
            onClick={() => setCurrentScreen('url-input')}
            className={'aspect-square rounded-2xl p-4 transition-all flex flex-col items-center justify-center relative ' + (scanData.url.active ? 'bg-black text-white' : 'bg-white border-2 border-black/12 text-black hover:border-black/30')}
          >
            {scanData.url.active && (
              <>
                <button onClick={(e) => { e.stopPropagation(); clearMethod('url'); }} className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <X size={14} className="text-white" />
                </button>
                <div className="absolute top-2 left-2">
                  <Check size={16} className="text-white" />
                </div>
              </>
            )}
            <Link size={32} className={(scanData.url.active ? 'text-white' : 'text-black') + ' mb-3'} strokeWidth={1.5} />
            <p className={'text-sm font-bold uppercase mb-1 ' + (scanData.url.active ? 'text-white' : 'text-black')}>URL</p>
            <p className={'text-xs text-center ' + (scanData.url.active ? 'text-white/70' : 'text-black/55')}>
              {scanData.url.active ? 'URL Added' : 'Paste Link'}
            </p>
          </button>
        </div>
      </div>

      <div className="px-6 mt-6">
        <div className="bg-black/5 rounded-xl p-4 text-center">
          <p className="text-xs uppercase font-bold text-black/55 mb-2">
            {activeMethods === 0 ? 'No methods selected' : activeMethods + ' method' + (activeMethods > 1 ? 's' : '') + ' selected'}
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {scanData.url.active && <span className="px-2 py-1 bg-black text-white rounded text-xs uppercase font-bold">URL</span>}
            {scanData.tag.active && <span className="px-2 py-1 bg-black text-white rounded text-xs uppercase font-bold">Tag ({scanData.tag.images.length})</span>}
            {scanData.barcode.active && <span className="px-2 py-1 bg-black text-white rounded text-xs uppercase font-bold">Barcode</span>}
            {scanData.photos.active && <span className="px-2 py-1 bg-black text-white rounded text-xs uppercase font-bold">Photos ({scanData.photos.images.length})</span>}
          </div>
        </div>
      </div>

      <div className="px-6 mt-6">
        <button
          onClick={handleAnalyze}
          disabled={!canAnalyze}
          className={'w-full py-4 rounded-2xl text-base font-bold uppercase transition-all ' + (canAnalyze ? 'bg-black text-white hover:bg-black/90' : 'bg-black/10 text-black/30 cursor-not-allowed')}
        >
          Analyze Garment
        </button>
        <p className="text-xs text-center text-black/55 mt-3">
          {canAnalyze ? 'Ready to analyze with ' + activeMethods + ' data source' + (activeMethods > 1 ? 's' : '') : 'Select at least one method to continue'}
        </p>
      </div>

      <div className="px-6 mt-6">
        <div className="bg-black/5 rounded-xl p-4 border border-black/10">
          <p className="text-xs uppercase font-bold text-black/55 mb-2">TIP FOR BEST ACCURACY</p>
          <p className="text-sm text-black/72">
            Combine multiple methods for higher accuracy. URL + Tag photos gives the most reliable results.
          </p>
        </div>
      </div>
    </div>

      {errorDialog && (
        <div className="fixed inset-0 z-50 flex items-end justify-center px-4 pb-8 bg-black/40" onClick={() => setErrorDialog(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={22} className="text-black flex-shrink-0 mt-0.5" />
              <p className="text-sm text-black leading-relaxed">{errorDialog}</p>
            </div>
            <button
              onClick={() => setErrorDialog(null)}
              className="w-full py-3 bg-black text-white rounded-xl text-sm font-bold uppercase"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ScanScreenQuadrant;
